import { faArrowLeftLong } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import RealLogo from '../../assets/img/real-logo.svg';
import useDeviceType from '../../hooks/useDeviceType';
import useNeoLeo from '../../hooks/useNeoLeo';
import {
  FieldObjectTypeEnum,
  Question,
  SectionProgressDtoStatusEnum,
} from '../../openapi/atlantis';
import { ContentItem } from '../../openapi/sherlock';
import { StyleTemplateEnum } from '../../openapi/yenta';
import { toggleCollapsed } from '../../slices/ApplicationSlice';
import { signOutUser } from '../../slices/AuthSlice';
import { setShowShareablesModal } from '../../slices/LeoSlice';
import { useAppDispatch, useAppSelector } from '../../slices/store';
import { ConfigDrivenQuestionTemplate } from '../../types';
import { cn } from '../../utils/classUtils';
import { getFullName } from '../../utils/commonUtils';
import {
  ApplicationStepWithNewContent,
  getOnboardingPreviousQuestionId,
  getPreviousQuestionId,
} from '../../utils/ConfigDrivenFormUtils';
import { isSectionIntro } from '../../utils/TypeUtils';
import Avatar from '../Avatar';
import Button from '../Button';
import NeoLeoGenericShareableModal from '../NeoLeo/Shareables/NeoLeoGenericSharableModal';
import VerticalStepProgressV2 from '../StepProgress/V2/VerticalStepProgressV2';

interface ConfigDrivenContainerLayoutV2Props {
  children: React.ReactNode;
  config?: ApplicationStepWithNewContent<Question>;
  onNavigateToQuestion?: (questionId: string) => void;
}

const ConfigDrivenContainerLayoutV2: React.FC<
  ConfigDrivenContainerLayoutV2Props
> = ({ children, onNavigateToQuestion, config }) => {
  const { isDesktop } = useDeviceType();
  const { applicationId } = useParams();
  const {
    auth: { authUserDetail },
    application: { progress, currentQuestion, isCollapsed },
    leo: { showShareablesModal },
  } = useAppSelector((state) => ({
    auth: state.auth,
    application: state.application,
    leo: state.leo,
  }));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const neoLeo = useNeoLeo({ notificationsLength: 0 });

  const activeSection = (progress?.sectionProgresses || [])?.find(
    (section) =>
      section?.status === SectionProgressDtoStatusEnum.InProgress ||
      section?.status === SectionProgressDtoStatusEnum.NotStarted,
  );
  const activeQuestionIndex = (
    activeSection?.questionProgresses || []
  )?.findIndex(
    (question) => question?.id === (currentQuestion as ContentItem)?.id,
  );
  const activeQuestionNumber = (activeQuestionIndex || 0) + 1;
  const totalQuestionsCount = activeSection?.questionProgresses?.length || 0;
  const isQuestionCountVisible =
    (activeSection?.questionProgresses?.length || 0) > 2 &&
    !isSectionIntro(config!);

  const isFieldTypeSignature = useMemo(() => {
    return config?.content?.fields?.some(
      (field) => field.type === FieldObjectTypeEnum.SignatureRequest,
    );
  }, [config?.content?.fields]);

  const prevQuestionId = applicationId
    ? getOnboardingPreviousQuestionId(
        progress?.sectionProgresses || [],
        config?.id,
      )
    : getPreviousQuestionId(
        progress?.sectionProgresses || [],
        config?.content.id,
      );

  // Check if this is agent onboarding and not the first question
  const isAgentOnboarding =
    config?.content?.style?.template ===
    (ConfigDrivenQuestionTemplate.TEMPLATE_3 as unknown as StyleTemplateEnum);

  const isFirstQuestion =
    activeQuestionNumber === 1 &&
    (progress?.sectionProgresses?.[0]?.questionProgresses?.length || 0) > 0;

  // For agent onboarding, find previous question manually if prevQuestionId is null
  const getAgentOnboardingPreviousQuestionId = () => {
    if (!isAgentOnboarding || !progress?.sectionProgresses) return null;

    const allQuestions = progress.sectionProgresses.flatMap(
      (section) => section.questionProgresses || [],
    );

    const currentIndex = allQuestions.findIndex((q) => q.id === config?.id);
    return currentIndex > 0 ? allQuestions[currentIndex - 1].id : null;
  };

  const effectivePrevQuestionId =
    isAgentOnboarding && !prevQuestionId
      ? getAgentOnboardingPreviousQuestionId()
      : prevQuestionId;

  // For agent onboarding, show back button unless it's the very first question or signature field
  const isPreviousButtonVisibleForMobileView = isAgentOnboarding
    ? !!effectivePrevQuestionId && !isFirstQuestion && !isFieldTypeSignature
    : !!prevQuestionId && !isFieldTypeSignature;

  const handleOnPrev = async (questionId: string) => {
    setIsLoading(true);
    await onNavigateToQuestion?.(questionId);
    setIsLoading(false);
  };

  return (
    <div className='fixed top-0 left-0 right-0 bottom-0 flex flex-col h-full max-h-screen overflow-hidden bg-white z-10'>
      {/* Top Header */}
      <div className='flex flex-row items-center justify-between lg:py-4 sm:py-2 sm:px-8 bg-white sm:border-b sm:border-reskin-grey-300'>
        <img
          src={RealLogo}
          alt='logo'
          width={!isDesktop ? 80 : 100}
          height={!isDesktop ? 20 : 34}
          className='hidden sm:block'
        />
        <div className='flex-row items-center gap-x-4 hidden sm:flex'>
          {/* TODO: Add in POST MVP */}
          {/* {neoLeo.NeoLeoIcon} */}
          <div className='flex flex-row items-center space-x-2.5 px-4 py-1 bg-reskin-slate-200 rounded-md'>
            <Avatar
              name={getFullName({
                firstName: authUserDetail?.firstName,
                lastName: authUserDetail?.lastName,
              })}
              imageUrl={authUserDetail?.avatar}
              size='md'
              variant='circle'
            />
            <p>
              {getFullName({
                firstName: authUserDetail?.firstName,
                lastName: authUserDetail?.lastName,
              })}
            </p>
          </div>
          <Button
            label='Logout'
            variant='primary-outline'
            leftIcon={<FontAwesomeIcon icon={faArrowRightFromBracket} />}
            onPress={async () => {
              const isAgentOnboarding =
                location.pathname.includes('/agent-onboarding');

              await dispatch(signOutUser());

              // Check if we should redirect to agent login
              if (isAgentOnboarding) {
                navigate('/agent-login');
              } else {
                navigate('/');
              }
            }}
            type='button'
            containerStyle='!rounded-[5px] !border-reskin-regent-400 !px-4 !py-2.5'
            textStyle='!font-inter-medium !font-medium !text-sm !text-reskin-primary-dark'
          />
        </div>
      </div>
      {/* Main Content */}
      <div className='flex flex-row flex-grow h-full overflow-hidden'>
        {/* Left Sidebar */}
        <div
          className={cn(
            'sm:flex flex-col w-[278px] h-full max-h-full border-r border-reskin-grey-300 overflow-y-auto hidden',
            {
              'w-auto': isCollapsed,
            },
          )}
        >
          <VerticalStepProgressV2
            progress={{
              sectionProgresses: [
                {
                  name: 'Create Account',
                  status: SectionProgressDtoStatusEnum.Completed,
                  questionProgresses: [],
                },
                ...(progress?.sectionProgresses || []),
              ],
            }}
            onNavigateToQuestion={(questionId: string) => {
              // Handle navigation to question
              if (onNavigateToQuestion) {
                onNavigateToQuestion(questionId);
              }
            }}
            setIsCollapsed={() => dispatch(toggleCollapsed())}
            isCollapsed={isCollapsed}
          />
        </div>
        {/* Right Content */}
        <div className='flex flex-col flex-grow overflow-hidden'>
          <div className='flex flex-col px-5 py-4 border-b border-reskin-slate-200 bg-blue-100 sm:bg-white'>
            <div
              className={cn(
                'flex flex-row justify-between sm:justify-start items-center',
                {
                  'sm:divide-x-2 sm:divide-reskin-slate-200':
                    isQuestionCountVisible,
                },
              )}
            >
              <div
                className={cn('flex flex-row items-center', {
                  'gap-x-3': isPreviousButtonVisibleForMobileView,
                })}
              >
                {isPreviousButtonVisibleForMobileView && (
                  <FontAwesomeIcon
                    icon={faArrowLeftLong}
                    size='xl'
                    className={cn('text-reskin-primary-dark sm:hidden', {
                      'opacity-50': isLoading,
                    })}
                    onClick={() => {
                      if (isLoading) {
                        return;
                      }

                      if (effectivePrevQuestionId) {
                        handleOnPrev(effectivePrevQuestionId);
                      }
                    }}
                  />
                )}
                <p
                  className={cn(
                    'font-inter font-semibold text-base text-reskin-slate-900',
                    {
                      'pr-3': isQuestionCountVisible,
                    },
                  )}
                >
                  {activeSection?.name ?? config?.sectionName}
                </p>
              </div>

              {isQuestionCountVisible && (
                <p className='font-inter font-semibold text-sm text-reskin-slate-500 pl-3 hidden sm:block'>{`Question ${activeQuestionNumber} of ${totalQuestionsCount}`}</p>
              )}
              <FontAwesomeIcon
                icon={faArrowRightFromBracket}
                size='xl'
                className='text-reskin-primary-dark sm:hidden'
                onClick={async () => {
                  const isAgentOnboarding =
                    location.pathname.includes('/agent-onboarding');

                  await dispatch(signOutUser());

                  // Check if we should redirect to agent login
                  if (isAgentOnboarding) {
                    navigate('/agent-login');
                  } else {
                    navigate('/');
                  }
                }}
              />
            </div>
          </div>
          <div className='flex flex-col flex-grow overflow-y-auto'>
            {/* Render the children passed to this component */}
            <div
              className={cn(
                'flex flex-col flex-grow bg-white sm:p-12 p-6 gap-6 w-full h-full relative',
                {
                  'overflow-y-auto': !isFieldTypeSignature,
                },
              )}
            >
              {children}
              {/* TODO: Add in POST MVP */}
              {/* {neoLeo.NeoLeoMobileIcon} */}
            </div>
          </div>
        </div>
        {neoLeo.NeoLeoSidebar}
        {neoLeo.NeoLeoMobileActionBar}
        {showShareablesModal && (
          <NeoLeoGenericShareableModal
            closeModal={() => dispatch(setShowShareablesModal(false))}
          />
        )}
      </div>
    </div>
  );
};

export default ConfigDrivenContainerLayoutV2;
